import { useEffect, useRef, useState } from "react";

export interface CountUpProps {
  /** The real value. Non-numeric input (e.g. an em dash while loading) renders unchanged. */
  value: number;
  durationMs?: number;
  className?: string;
}

/** Decelerating curve — fast at first, easing into the final number. */
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * Animates a stat from 0 to its value on mount — and ONLY on mount.
 *
 * Driven by requestAnimationFrame against real elapsed time rather than a fixed per-frame step,
 * so the duration holds on a 120Hz display and on a throttled background tab alike.
 *
 * Only the text content changes — no transform, no layout property, nothing that can reflow the
 * card. The value is rendered with tabular figures by the caller, so the digits do not jitter as
 * they climb.
 *
 * The mount-only rule matters now that the dashboard polls. The earlier version keyed its effect
 * on `value`, so every change restarted the climb FROM ZERO: a counter ticking 1,240 -> 1,241 on
 * a background refresh would replay the whole animation every interval. That turns a one-time
 * flourish into a recurring distraction, and it also misreads a +1 as a full recount. Later
 * values are therefore applied directly, with no animation.
 */
export function CountUp({ value, durationMs = 750, className }: CountUpProps) {
  const [display, setDisplay] = useState(value);
  const frame = useRef<number>();
  /**
   * The value present when this stat first appeared. Captured in a ref rather than tracked in
   * the effect's deps so that later updates cannot re-trigger the climb.
   */
  const initial = useRef(value);

  // The one-time climb. Deliberately NOT keyed on `value` — see the note above.
  useEffect(() => {
    const target = initial.current;
    if (!Number.isFinite(target)) return;

    // prefers-reduced-motion means show the answer, not a slower animation.
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced || target === 0) {
      setDisplay(target);
      return;
    }

    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs);
      setDisplay(Math.round(easeOutCubic(progress) * target));
      if (progress < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);

    return () => {
      if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    };
  }, [durationMs]);

  // Every later value — i.e. every background poll that actually changed something — lands
  // directly. Any climb still in flight is cancelled first so it cannot overwrite the newer
  // number a frame later.
  useEffect(() => {
    if (value === initial.current) return;
    if (frame.current !== undefined) {
      cancelAnimationFrame(frame.current);
      frame.current = undefined;
    }
    if (Number.isFinite(value)) setDisplay(value);
  }, [value]);

  return <span className={className}>{display.toLocaleString()}</span>;
}
